import React, { useEffect, useMemo, useRef, useState } from "react";
import {
  buildPassportComplianceChecks,
  COMPLIANCE_ROW_PAUSE_MS,
  COMPLIANCE_STATUS,
  COMPLIANCE_TYPE_MS,
} from "../config/passportComplianceChecks";
import "./PassportComplianceCheckPreview.css";

function prefersReducedMotion() {
  if (typeof window === "undefined" || !window.matchMedia) return false;
  return window.matchMedia("(prefers-reduced-motion: reduce)").matches;
}

function sleep(ms, signal) {
  return new Promise((resolve, reject) => {
    if (signal?.aborted) {
      reject(new DOMException("Aborted", "AbortError"));
      return;
    }
    const id = setTimeout(resolve, ms);
    const onAbort = () => {
      clearTimeout(id);
      reject(new DOMException("Aborted", "AbortError"));
    };
    signal?.addEventListener("abort", onAbort, { once: true });
  });
}

/**
 * Canvas compliance checklist while passport generate is running.
 * Values type out row-by-row; the card scrolls to keep the active row in view.
 */
const PassportComplianceCheckPreview = ({
  documentName = "Passport photo",
  country = "—",
  documentType = "Passport",
  qualityLabel = "300 DPI",
  backgroundHex = "#FFFFFF",
  widthMm = 35,
  heightMm = 45,
  active = true,
  complete = false,
}) => {
  const checks = useMemo(
    () =>
      buildPassportComplianceChecks({
        documentName,
        country,
        documentType,
        qualityLabel,
        backgroundHex,
        widthMm,
        heightMm,
      }),
    [
      documentName,
      country,
      documentType,
      qualityLabel,
      backgroundHex,
      widthMm,
      heightMm,
    ]
  );

  const cardRef = useRef(null);
  const rowRefs = useRef({});

  const [statuses, setStatuses] = useState(() =>
    Object.fromEntries(
      checks.map((c) => [
        c.id,
        complete
          ? c.statusOnDone || COMPLIANCE_STATUS.PASS
          : COMPLIANCE_STATUS.PENDING,
      ])
    )
  );
  const [typed, setTyped] = useState(() =>
    Object.fromEntries(
      checks.map((c) => [c.id, complete && c.valueType === "text" ? c.value : ""])
    )
  );
  const [swatchVisible, setSwatchVisible] = useState(() =>
    Object.fromEntries(checks.map((c) => [c.id, Boolean(complete)]))
  );
  const [activeId, setActiveId] = useState(null);

  const scrollRowIntoView = (id) => {
    const row = rowRefs.current[id];
    const card = cardRef.current;
    if (!row || !card) return;
    const reduce = prefersReducedMotion();
    row.scrollIntoView({
      block: "nearest",
      behavior: reduce ? "auto" : "smooth",
    });
  };

  useEffect(() => {
    if (complete) {
      setStatuses(
        Object.fromEntries(
          checks.map((c) => [c.id, c.statusOnDone || COMPLIANCE_STATUS.PASS])
        )
      );
      setTyped(
        Object.fromEntries(
          checks.map((c) => [c.id, c.valueType === "text" ? c.value : ""])
        )
      );
      setSwatchVisible(Object.fromEntries(checks.map((c) => [c.id, true])));
      setActiveId(null);
      return undefined;
    }

    if (!active) return undefined;

    const controller = new AbortController();
    const { signal } = controller;
    const reduce = prefersReducedMotion();

    setStatuses(
      Object.fromEntries(checks.map((c) => [c.id, COMPLIANCE_STATUS.PENDING]))
    );
    setTyped(Object.fromEntries(checks.map((c) => [c.id, ""])));
    setSwatchVisible(Object.fromEntries(checks.map((c) => [c.id, false])));
    setActiveId(null);

    const run = async () => {
      try {
        for (const check of checks) {
          if (signal.aborted) return;

          setActiveId(check.id);
          setStatuses((prev) => ({
            ...prev,
            [check.id]: COMPLIANCE_STATUS.CHECKING,
          }));

          // Let the row mount/update before scrolling.
          await sleep(52, signal);
          scrollRowIntoView(check.id);

          if (check.valueType === "swatch") {
            await sleep(reduce ? 225 : 898, signal);
            setSwatchVisible((prev) => ({ ...prev, [check.id]: true }));
            await sleep(reduce ? 150 : 412, signal);
          } else {
            const full = String(check.value ?? "");
            if (reduce) {
              setTyped((prev) => ({ ...prev, [check.id]: full }));
              await sleep(299, signal);
            } else {
              let built = "";
              for (let i = 0; i < full.length; i += 1) {
                if (signal.aborted) return;
                built += full[i];
                setTyped((prev) => ({ ...prev, [check.id]: built }));
                // Keep the typing row visible as text grows.
                if (i === 0 || i % 8 === 0) scrollRowIntoView(check.id);
                await sleep(COMPLIANCE_TYPE_MS, signal);
              }
            }
          }

          setStatuses((prev) => ({
            ...prev,
            [check.id]: check.statusOnDone || COMPLIANCE_STATUS.PASS,
          }));
          await sleep(COMPLIANCE_ROW_PAUSE_MS, signal);
        }
        setActiveId(null);
      } catch (err) {
        if (err?.name !== "AbortError") throw err;
      }
    };

    run();

    return () => {
      controller.abort();
    };
  }, [active, complete, checks]);

  return (
    <div className="appm-compliance">
      <div className="appm-compliance__header">
        <span className="appm-compliance__icon" aria-hidden="true">
          <svg viewBox="0 0 24 24" width="18" height="18" fill="none">
            <circle
              cx="10.5"
              cy="10.5"
              r="6.5"
              stroke="currentColor"
              strokeWidth="2"
            />
            <path
              d="M15.5 15.5L20 20"
              stroke="currentColor"
              strokeWidth="2"
              strokeLinecap="round"
            />
            <path
              d="M8.2 10.5l1.5 1.5 3.2-3.2"
              stroke="currentColor"
              strokeWidth="2"
              strokeLinecap="round"
              strokeLinejoin="round"
            />
          </svg>
        </span>
        <p
          className={`appm-compliance__title${
            complete ? " appm-compliance__title--complete" : ""
          }`}
        >
          {complete
            ? `Compliance complete (${documentName}):`
            : `Checking for compliance (${documentName}):`}
        </p>
      </div>

      <div
        ref={cardRef}
        className="appm-compliance__card"
        role="status"
        aria-live="polite"
      >
        <ul className="appm-compliance__list">
          {checks.map((check) => {
            const status = statuses[check.id] || COMPLIANCE_STATUS.PENDING;
            const isPending = status === COMPLIANCE_STATUS.PENDING;
            const isChecking = status === COMPLIANCE_STATUS.CHECKING;
            const isPass = status === COMPLIANCE_STATUS.PASS;
            const isNeutral = status === COMPLIANCE_STATUS.NEUTRAL;
            const isActive = activeId === check.id;
            const typedValue = typed[check.id] || "";
            const showSwatch = swatchVisible[check.id];

            return (
              <li
                key={check.id}
                ref={(el) => {
                  rowRefs.current[check.id] = el;
                }}
                className={`appm-compliance__row${
                  isActive ? " is-active" : ""
                }${isPending ? " is-pending" : ""}`}
              >
                <div className="appm-compliance__copy">
                  <p className="appm-compliance__label">{check.label}</p>
                  {check.valueType === "swatch" ? (
                    showSwatch ? (
                      <span
                        className="appm-compliance__swatch appm-compliance__swatch--in"
                        style={{ backgroundColor: check.value }}
                        title={check.value}
                      />
                    ) : (
                      <span className="appm-compliance__value-placeholder">
                        {isChecking ? "Detecting…" : ""}
                      </span>
                    )
                  ) : typedValue ? (
                    <p className="appm-compliance__value">
                      {typedValue}
                      {isChecking ? (
                        <span className="appm-compliance__caret" aria-hidden="true">
                          |
                        </span>
                      ) : null}
                    </p>
                  ) : (
                    <span className="appm-compliance__value-placeholder">
                      {isChecking ? "Analyzing…" : ""}
                    </span>
                  )}
                </div>

                <span
                  className={`appm-compliance__status${
                    isChecking ? " appm-compliance__status--spin" : ""
                  }${isPass ? " appm-compliance__status--pass" : ""}${
                    isNeutral ? " appm-compliance__status--neutral" : ""
                  }${isPending ? " appm-compliance__status--pending" : ""}`}
                  aria-label={
                    isPending
                      ? "Waiting"
                      : isChecking
                        ? "Checking"
                        : isPass
                          ? "Passed"
                          : "Noted"
                  }
                >
                  {isChecking ? (
                    <svg
                      className="appm-compliance__spinner"
                      viewBox="0 0 20 20"
                      width="18"
                      height="18"
                      aria-hidden="true"
                    >
                      <circle
                        className="appm-compliance__spinner-track"
                        cx="10"
                        cy="10"
                        r="7.5"
                        fill="none"
                      />
                      <circle
                        className="appm-compliance__spinner-arc"
                        cx="10"
                        cy="10"
                        r="7.5"
                        fill="none"
                      />
                    </svg>
                  ) : isPass ? (
                    "✓"
                  ) : isNeutral ? (
                    "–"
                  ) : null}
                </span>
              </li>
            );
          })}
        </ul>
      </div>
    </div>
  );
};

export default PassportComplianceCheckPreview;
